/* NearbyHelpers — surfaces users within ~15 km of a mission that have
   opted-in to "Presenza sulla mappa". Each entry links to the public
   profile so the mission creator can quickly ping a specific volunteer
   or verify the community around them.

   The endpoint is polled once on mount (no auto-refresh — the API is
   cheap but this panel is auxiliary; parent refreshes on join/leave
   events already).
*/
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export default function NearbyHelpers({ missionId }) {
  const [helpers, setHelpers] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/missions/v2/${missionId}/nearby-helpers`);
        if (!cancelled) setHelpers(data.helpers || []);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [missionId]);

  if (error) return null;
  if (helpers === null) {
    return (
      <div className="mt-8" data-testid="nearby-helpers-loading">
        <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-3">
          Chi può intervenire
        </h3>
        <div className="rounded-2xl border border-[#F0EBE3] p-4 text-center text-[13px] text-[#9A9A9A]">
          Cerco utenti nelle vicinanze…
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="nearby-helpers">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] inline-flex items-center gap-1.5">
          <Users className="h-3 w-3" /> Chi può intervenire · 15 km
        </h3>
        <span className="text-[11px] text-[#9A9A9A]">{helpers.length}</span>
      </div>

      {helpers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5DECF] p-4 text-center text-[13px] text-[#9A9A9A]">
          Nessuno online adesso nelle vicinanze.
          <br />
          <span className="text-[11px] mt-1 inline-block">
            Chiedi ai tuoi contatti di attivare la "Presenza sulla mappa".
          </span>
        </div>
      ) : (
        <div className="grid gap-2">
          {helpers.slice(0, 8).map((h) => (
            <Link
              key={h.id}
              to={`/app/u/${h.id}`}
              data-testid={`helper-${h.id}`}
              className="group flex items-center gap-3 rounded-2xl bg-white border border-[#F0EBE3] px-3 py-2.5 hover:bg-[#FAF7F0] transition"
            >
              <div className="relative shrink-0">
                <div className="h-11 w-11 rounded-full overflow-hidden bg-[#F0EBE3] flex items-center justify-center">
                  {h.photo_url ? (
                    <img src={h.photo_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[13px] font-semibold text-[#B75E2A]">
                      {(h.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#34D399] border-2 border-white animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight truncate">
                  {h.name || "Utente"}
                  {h.dog?.name && (
                    <span className="text-[11px] text-[#7A7A7A] font-normal ml-1.5">
                      · 🐕 {h.dog.name}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#7A7A7A] inline-flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3" />
                  {h.distance_km} km {h.city && `· ${h.city}`}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#B5AE9E] shrink-0 group-hover:text-[#3A3A3A]" />
            </Link>
          ))}
          {helpers.length > 8 && (
            <div className="text-center text-[12px] text-[#9A9A9A] mt-1">
              +{helpers.length - 8} altri online nella zona
            </div>
          )}
        </div>
      )}
    </div>
  );
}
